import { InfiniteData } from '@tanstack/react-query'
import { compareDesc } from 'date-fns'
import { useMemo } from 'react'
import { usePaginatedBoards } from '@opallabs/api'
import {
  OpalApiComponent,
  findResourceOfType,
  OpalApiResponseDocument,
} from '@opallabs/api/v3'
import { queryKeys } from '../../queryKeys'

type BoardResponseDocument = OpalApiResponseDocument<'ReadBoardsV3', 200>

type UseBoardListProps = Readonly<{
  isArchived: boolean
  isStarred: boolean
  workspaceUuid: string
}>

/**
 * Query to supply data to the Boards index page (/boards)
 * Uses pagination to handle workspaces with many boards.
 */
export const useBoardList = ({
  isArchived,
  isStarred,
  workspaceUuid,
}: UseBoardListProps) => {
  const { fetchingComplete, isFetchingMore, boards } = usePaginatedBoards({
    workspaceId: workspaceUuid,
    ...boardListFields,
    filter: {
      is_archived: isArchived,
      is_starred: isStarred,
    },
    select: selectPaginatedBoardList,
  })

  return useMemo(
    () => ({
      isSuccess: fetchingComplete,
      isLoading: !fetchingComplete,
      isFetchingMore,
      data: boards ?? [],
    }),
    [fetchingComplete, isFetchingMore, boards]
  )
}

export const boardListQueryKey = (workspaceUuid: string, isArchived: boolean) =>
  queryKeys.boards(workspaceUuid, {
    filter: { is_archived: isArchived },
    ...boardListFields,
  })

const boardListFields = {
  fields: {
    asset_reference: ['asset', 'full_url'].join(','),
    board: [
      'asset_reference',
      'board_user_setting',
      'created_at',
      'created_by_user',
      'description',
      'is_archived',
      'is_public',
      'title',
    ].join(','),
  },
  include: [
    'asset_reference' as const,
    'board_user_setting' as const,
    'created_by_user' as const,
  ],
}

const selectPaginatedBoardList = (
  infiniteData?: InfiniteData<BoardResponseDocument>
) => {
  if (!infiniteData) return []

  const allBoards: Array<OpalApiComponent<'V3', 'board'>> = []
  const allIncluded: BoardResponseDocument['included'] = []

  for (const page of infiniteData.pages) {
    allBoards.push(...page.data)
    if (page.included) {
      allIncluded.push(...page.included)
    }
  }

  const sorted = allBoards.sort((a, b) =>
    compareDesc(
      new Date(a.attributes.created_at),
      new Date(b.attributes.created_at)
    )
  )

  return sorted.map((board) => transformBoard(board, allIncluded))
}

const transformBoard = (
  board: OpalApiComponent<'V3', 'board'>,
  included: BoardResponseDocument['included']
) => {
  const boardAssetObject = included?.find(
    findResourceOfType(
      'asset_reference',
      'V3',
      board.relationships.asset_reference?.data?.id
    )
  )

  const assetReferenceUuid = boardAssetObject?.relationships?.asset?.data?.id
  const fullUrl = boardAssetObject?.attributes?.full_url

  const creator = included?.find(
    findResourceOfType(
      'user',
      'V3',
      board.relationships.created_by_user.data.id
    )
  )

  const userSetting = included?.find(
    findResourceOfType(
      'board_user_setting',
      'V3',
      board.relationships.board_user_setting?.data.id
    )
  )

  return {
    createdByUserId: creator?.attributes.legacy_id,
    description: board.attributes.description,
    id: board.id,
    imageSource: fullUrl?.startsWith('blob:') ? fullUrl : null,
    imageUuid: assetReferenceUuid ?? null,
    isArchived: board.attributes.is_archived,
    isPending: Boolean(board.meta?.pending),
    isPublic: board.attributes.is_public,
    isStarred: userSetting?.attributes?.is_starred,
    title: board.attributes.title,
  }
}

// Supplies MoveCollectionToBoardModal
export const useAddToBoardList = (workspaceUuid: string) => {
  const { fetchingComplete, isFetching, isFetchedAfterMount, boards } =
    usePaginatedBoards({
      workspaceId: workspaceUuid,
      ...addToBoardFields,
      select: selectPaginatedAddToBoardData,
    })

  return useMemo(
    () => ({
      isSuccess: fetchingComplete,
      isLoading: !fetchingComplete,
      isFetching,
      isFetchedAfterMount,
      data: boards ?? [],
    }),
    [fetchingComplete, isFetching, isFetchedAfterMount, boards]
  )
}

export const addToBoardListQueryKey = (workspaceUuid: string) =>
  queryKeys.boards(workspaceUuid, addToBoardFields)

const addToBoardFields = {
  fields: {
    board: [
      'created_at',
      'created_by_user',
      'description',
      'is_public',
      'title',
      'default_story',
    ].join(','),
  },
  include: ['default_story' as const],
}

const selectPaginatedAddToBoardData = (
  infiniteData?: InfiniteData<BoardResponseDocument>
) => {
  if (!infiniteData) return []

  const allBoards: Array<OpalApiComponent<'V3', 'board'>> = []
  for (const page of infiniteData.pages) {
    allBoards.push(...page.data)
  }

  const sorted = allBoards.sort((a, b) =>
    compareDesc(
      new Date(a.attributes.created_at),
      new Date(b.attributes.created_at)
    )
  )

  return sorted.map((board) => ({
    id: board.id,
    title: board.attributes.title,
    isPending: Boolean(board.meta?.pending),
    description: board.attributes.description,
    isPublic: board.attributes.is_public,
    createdByUserId: board.relationships.created_by_user.data.id,
    defaultStoryId: board.relationships.default_story?.data?.id || null,
  }))
}
